import React, { useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { adaptHeightOnWidget, formatNumber, i18nIntl, isNotEmpty } from '../../lib/ep.js';
import { useCSS } from '../../lib/hooks.js';

import { localizeFields } from '../builder/DynamicFields.js';
import ToggleButton from '../builder/ToggleButton.js';

import LeveledHTMLText from './helpers/LeveledHTMLText.js';

import { getAvailableIntervals, getFilteredMatches, getIntervalFromResults, getTeamInfoByID, getTeamName } from './GuessScoreClient.js';
import { TeamInfo } from './GuessScoreResume.js';

const fake_results_x_match = 30;

const MatchTop5 = ({ results, teams, ...props }) => {
	const type = props?.type ?? 'result';
	const is_backend = props?.is_backend ?? false;

	const sorted_results = [ ...results ].sort((a, b) => {
		if(a.total < b.total) return 1;
		if(a.total > b.total) return -1;
		if(type == 'margin') {
			if(a.interval < b.interval) return -1;
			if(a.interval > b.interval) return 1;
		}
		return 0;
	});

	const max_results = is_backend ? 1000000 : 5;
	const num_decimals = 1;
	const total_users = props?.total ?? 1;

	let total_bets = 0;
	for (let i = 0; i < results.length; i++) {
		total_bets += parseInt(results[i].total);
	}

	if(type == 'margin') {
		const team_1 = props?.team_1;
		const team_2 = props?.team_2;

		return (
			<div class="list double">
				<div>
					<p class="title">
						<strong>{getTeamName(team_1)}:</strong>
					</p>

					<ul>
						{sorted_results.filter((t) => (t.winner == team_1.id)).map((r, index) => {
							return (
								<li key={`r_${index}`}>
									<strong>{r.interval}</strong>
									: {formatNumber(parseInt(r.total) / total_bets * 100, num_decimals)}%
								</li>
							)
						})}
					</ul>
				</div>

				<div>
					<p class="title">
						<strong>{getTeamName(team_2)}:</strong>
					</p>

					<ul>
						{sorted_results.filter((t) => (t.winner == team_2.id)).map((r, index) => {
							return (
								<li key={`r_${index}`}>
									<strong>{r.interval}</strong>
									: {formatNumber(parseInt(r.total) / total_bets * 100, num_decimals)}%
								</li>
							)
						})}
					</ul>
				</div>
			</div>
		)
	}

	return (
		<ul class="list">
			{sorted_results.slice(0, max_results).map((r, index) => {
				return (
					<li key={`r_${index}`}>
						<strong>{`${r.result_1}-${r.result_2}`}</strong>
						: {formatNumber(parseInt(r.total) / total_bets * 100, num_decimals)}%
					</li>
				)
			})}
		</ul>
	)
};

const SingleStat = ({ match, results, team_1, team_2, colors, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ view_detail, setViewDetail ] = useState(false);

	const match_index = props?.match_index ?? 0;
	const type = props?.type ?? 'result';
	const is_backend = props?.is_backend ?? false;
	const is_builder = props?.is_builder ?? false;
	useEffect(() => {
		setTimeout(() => {
			adaptHeightOnWidget();
		}, 0);
	}, [ view_detail ]);

	return (
		<div
			onClick={() => setViewDetail(!view_detail)}
		>
			<TeamInfo
				team={team_1}
				direction={-1}
			/>

			<span class="vs">{i18n("vs")}</span>

			<TeamInfo
				team={team_2}
			/>

			<div class="stat">
				<div class="stat_row">
					{['1', 'X', '2'].map((result) => {
						const is_user = (match.user == result) ? true : false;
						const percent = 100 * match[result] / match.total;
						const classes = ['stats_bar', `is_${result}`];
						if(is_user) classes.push('is_user');

						return (
							<span
								key={`match_${match_index}_${result}`}
								class={classes.join(" ")}
								style={{
									width:`${percent}%`,
									backgroundColor:colors[result].color,
								}}
							>
								{(percent > 5) && (
									<strong
										style={{
											color:colors[result].text_color,
										}}
									>{formatNumber(percent, 1)}%</strong>
								)}
							</span>
						)}
					)}
				</div>
			</div>

			<div class="detail">
				<ToggleButton
					active={view_detail}
					color={false}
					icon='knk-arrow-t-big'
					icon_off='knk-arrow-b-big'
				/>

				{(view_detail) && (
					<MatchTop5
						total={(is_builder === false) ? results.length : fake_results_x_match}
						results={results}
						teams={props.teams}
						type={type}
						team_1={team_1}
						team_2={team_2}
						is_backend={is_backend}
					/>
				)}
			</div>
		</div>
	)
};

export const GuessScoreStats = ({ stage, stats, stats_score, ...props }) => {
	useCSS('comp/guess_score.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const matches = getFilteredMatches(stage?.guess_score_matches ?? []);
	const type = stage?.guess_score_type ?? 'result';
	const is_backend = props?.is_backend ?? false;

	const colors = {
		'1':{
			order:1,
			color:stage?.guess_color_local_wins ?? 'rgba(var(--ep-green-color), 1)',
			text_color:'black',
			label:(isNotEmpty(stage, 'guess_label_local_wins')) ? stage.guess_label_local_wins : i18n("Local wins"),
			display:true,
		},
		'X':{
			order:2,
			color:stage?.guess_color_draw ?? 'rgba(var(--ep-orange-color), 1)',
			text_color:'black',
			label:(isNotEmpty(stage, 'guess_label_draw')) ? stage.guess_label_draw : i18n("***Draw"),
			display:(type == 'margin') ? false : true,
		},
		'2':{
			order:3,
			color:stage?.guess_color_visitor_wins ?? 'rgba(var(--ep-red-color), 1)',
			text_color:'white',
			label:(isNotEmpty(stage, 'guess_label_visitor_wins')) ? stage.guess_label_visitor_wins : i18n("Visitor wins"),
			display:true,
		},
	};

	const promotion = props?.promotion?.Promotion ?? {};
	const teams = localizeFields((promotion?.guess_score_teams ?? []), props.locale);

	const display_legend = props?.display_legend ?? false;
	if(stats.length == 0 && is_backend){
		return (
			<div class="guess_stats">{i18n("There are no results.")}</div>
		)
	}
	return (
		<>
			<div class="guess_stats">
				{stats.map((match_stat, m) => {
					const match = matches.find((m) => (m.id_1 == match_stat.id_1 && m.id_2 == match_stat.id_2));
					if(!match) return;

					const match_results = stats_score.filter((m) => (m.id_1 == match_stat.id_1 && m.id_2 == match_stat.id_2));
					return (
						<SingleStat
							key={`match_${m}`}
							team_1={getTeamInfoByID(teams, match_stat.id_1)}
							team_2={getTeamInfoByID(teams, match_stat.id_2)}
							match={match_stat}
							teams={teams}
							results={match_results}
							match_index={m}
							colors={colors}
							type={type}
							is_builder={props.is_builder}
							is_backend={props.is_backend}
							intl={props.intl}
						/>
					)
				})}
			</div>

			{(display_legend) && (
				<div class="color_legend">
					{(Object.values(colors)).sort((a, b) => {
						return (a.order < b.order) ? -1 : 1; 
					}).map((color) => {
						if((color?.display ?? true) === false) return null;

						return (
							<div key={`color_${color}`}>
								<span
									class="swatch"
									style={{
										display:'inline-block',
										width:'1.5em',
										height:'1.5em',
										backgroundColor:color.color,
									}}
								/>

								<strong>{color.label}</strong>
							</div>
						)
					})}
				</div>
			)}
		</>
	)
};

const GuessScoreFinal = ({ stage, ...props }) => {
	useCSS('comp/guess_score.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ is_loading, setIsLoading ] = useState(true);

	const is_builder = props?.is_builder ?? false;

	const matches = getFilteredMatches(stage?.guess_score_matches ?? []);
	const display_stats = stage?.guess_display_stats ?? true;

	let user_answer = props?.user_answer?.guess_score ?? {};
	if(is_builder) user_answer = Object.assign({}, ...matches.map(({ id, ...rest }) => ({ [id]:rest })));

	const stats_combined = useRef(props?.stats?.combined ?? []);
	const stats_score = useRef(props?.stats?.score ?? []);

	const type = stage?.guess_score_type ?? 'result';
	const step_interval = parseInt(stage?.guess_score_step_interval ?? '2');
	const max_interval = parseInt(stage?.guess_score_max_interval ?? '3');

	let intervals = [...Array(10).keys()];
	if(type == 'margin') intervals = getAvailableIntervals(step_interval, max_interval);

	const updateStatsWithActualParticipation = () => {
		if(is_builder) {
			const fake_total_plays = Math.round(1000 * Math.random());

			Object.keys(user_answer).map((match_id) => {
				const match = matches.find((m) => (m.id == match_id));
				const random_score = Math.round(100 * Math.random());

				const _t = {
					id_1:match.id_1,
					id_2:match.id_2,
					'1':0,
					'2':0,
					'X':0,
					total:fake_total_plays,
				};

				_t['1'] = Math.round(fake_total_plays * Math.random());
				if(type == 'margin') {
					_t['2'] = fake_total_plays - _t['1'];
				} else {
					_t['2'] = Math.round((fake_total_plays - _t['1']) * Math.random());
					_t['X'] = fake_total_plays - _t['1'] - _t['2'];
				}

				if(type == 'margin') {
					if(random_score <= 50) {
						_t.user = '1';
					} else {
						_t.user = '2';
					}
				} else {
					if(random_score < 33) {
						_t.user = '1';
					} else if(random_score > 33) {
						_t.user = '2';
					} else {
						_t.user = 'X';
					}
				}
				stats_combined.current.push(_t);

				[...Array(fake_results_x_match).keys()].map((foo) => {
					const max = Math.pow(10, parseInt(stage?.guess_score_num_digits ?? 1)) - 1;
					const random_res_1 = Math.floor(Math.random() * (max + 1));
					let random_res_2 = Math.floor(Math.random() * (max + 1));
					if(type == 'margin' && random_res_1 == random_res_2) {
						// we don't accept draws in margin type:
						random_res_2 += 1;
					}

					const winner = (random_res_1 > random_res_2) ? match.id_1 : match.id_2;
					const used_interval = (is_builder !== false) ? getIntervalFromResults(type, intervals, random_res_1, random_res_2) : false;

					// match_result already exist
					const already_match_result = stats_score.current.find((_match) => {
						if(type == 'margin') {
							if(winner != _match.winner) return false;
							if(used_interval != _match.interval) return false;
							return true;
						}

						if(match.id_1 != _match.id_1 || match.id_2 != _match.id_2) return false;
						if(match.result_1 != random_res_1 || match.result_2 != random_res_2) return false;
						return true;
					});

					if(already_match_result) {
						already_match_result.total += 1;
					} else {
						stats_score.current.push({
							id_1:match.id_1,
							id_2:match.id_2,
							result_1:random_res_1,
							result_2:random_res_2,
							winner,
							interval:used_interval,
							total:1,
						});
					}
				});
			});

			setIsLoading(false);
			return;
		}

		Object.keys(user_answer).map((match_id) => {
			const resultat = user_answer[match_id];
			const match = matches.find((m) => (m.id == match_id));
			if(!match) return;

			const team_1 = match.id_1;
			const team_2 = match.id_2;

			stats_combined.current.map((match_stat, s) => {
				if(match_stat.id_1 != team_1 || match_stat.id_2 != team_2) return;

				let match_result_type;
				if(stage.guess_score_type == 'margin') {
					if(team_1 == resultat.winner) {
						match_result_type = '1';
					} else if(team_2 == resultat.winner) {
						match_result_type = '2';
					}
				} else {
					if(resultat.id_1 > resultat.id_2) {
						match_result_type = '1';
					} else if(resultat.id_2 > resultat.id_1) {
						match_result_type = '2';
					} else {
						match_result_type = 'X';
					}
				}

				stats_combined.current[s].user = match_result_type;
				stats_combined.current[s][match_result_type] = (stats_combined.current[s][match_result_type] || 0) + 1;
				stats_combined.current[s].total = stats_combined.current[s]['1'] + stats_combined.current[s]['2'] + stats_combined.current[s]['X'];
			});

			let matchFound = false;
			stats_score.current.map((match_stat, s) => {
				if(match_stat.id_1 != team_1 || match_stat.id_2 != team_2) return;
				if(stage.guess_score_type != 'margin') {
					if(match_stat.result_1 != resultat.id_1 || match_stat.result_2 != resultat.id_2) return;
				} else {
					if(match_stat.winner != resultat.winner || match_stat.interval != resultat.interval) return;
				}
				stats_score.current[s]['total'] = parseInt(stats_score.current[s]['total']) + 1;
				matchFound = true;
			});

			if(!matchFound) {
				if(stage.guess_score_type != 'margin') {
					stats_score.current.push({
						id_1:team_1,
						id_2:team_2,
						result_1:resultat.id_1,
						result_2:resultat.id_2,
						total:1,
					});
				} else {
					stats_score.current.push({
						id_1:team_1,
						id_2:team_2,
						winner:resultat.winner,
						interval:resultat.interval,
						total:1,
					});
				}
			}
		});

		setIsLoading(false);
	};

	useEffect(() => {
		updateStatsWithActualParticipation();
	}, []);

	const main_description_placeholder = `<h1>${i18n("Thank you")}</h1><p>${i18n("Here are the statistics for each match.")}</p>`;
	const final_message = (isNotEmpty(stage, 'final_message')) ? stage.final_message : main_description_placeholder;

	if(is_loading === true) return null;

	return (
		<div>
			{(final_message != " ") && (
				<LeveledHTMLText>{final_message}</LeveledHTMLText>
			)}

			{(display_stats) && (
				<GuessScoreStats
					stage={stage}
					stats={stats_combined.current}
					stats_score={stats_score.current}
					promotion={props.promotion}
					display_legend={true}
					is_builder={is_builder}
					locale={props.locale}
					intl={props.intl}
				/>
			)}
		</div>
	);
};

export default injectIntl(GuessScoreFinal);